import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { I18nProvider } from '@/services/i18n';
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import LessonPlanBuilder from '@/pages/LessonPlanBuilder';
import Templates from '@/pages/Templates';
import SchoolSettings from '@/pages/SchoolSettings';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import Profile from '@/pages/Profile';
import Library from '@/pages/Library';
import SharedPlan from '@/pages/SharedPlan';
import Analytics from '@/pages/Analytics';
import WorksheetBuilder from '@/pages/WorksheetBuilder';
import WorksheetLibrary from '@/pages/WorksheetLibrary';
import QuestionPaperBuilder from '@/pages/QuestionPaperBuilder';
import AdminCurriculum from '@/pages/AdminCurriculum';
import AdminCurriculumLibrary from '@/pages/AdminCurriculumLibrary';
import SamplePaperBuilder from '@/pages/SamplePaperBuilder';
import Nanno from '@/pages/Nanno';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/builder"
        element={
          <ProtectedRoute>
            <LessonPlanBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/builder/:id"
        element={
          <ProtectedRoute>
            <LessonPlanBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <Templates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SchoolSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowIncompleteProfile>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Library />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route path="/shared/:token" element={<SharedPlan />} />
      <Route
        path="/worksheet"
        element={
          <ProtectedRoute>
            <WorksheetBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worksheet/:id"
        element={
          <ProtectedRoute>
            <WorksheetBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/worksheet-library"
        element={
          <ProtectedRoute>
            <WorksheetLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/question-paper"
        element={
          <ProtectedRoute>
            <QuestionPaperBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/question-paper/:id"
        element={
          <ProtectedRoute>
            <QuestionPaperBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/curriculum"
        element={
          <ProtectedRoute>
            <AdminCurriculum />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/library"
        element={
          <ProtectedRoute>
            <AdminCurriculumLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sample-paper"
        element={
          <ProtectedRoute>
            <SamplePaperBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sample-paper/:id"
        element={
          <ProtectedRoute>
            <SamplePaperBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nanno"
        element={
          <ProtectedRoute>
            <Nanno />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
